/*
 * Load more for product grids.
 *
 * Progressive enhancement over Shopify's pagination: the control is a real link to
 * the next page, so it works with the script blocked and stays crawlable. The script
 * intercepts the click, fetches that same page through the Section Rendering API and
 * appends its cards.
 *
 * A custom element rather than an init function, because Dawn's facets.js replaces
 * the whole product grid container when a filter changes — a fresh element upgrades
 * itself on insertion, where a one-shot listener would be lost.
 */
class MgbLoadMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-load-more-button]');
    this.shownCount = this.querySelector('[data-load-more-shown]');
    this.isLoading = false;

    this.button?.addEventListener('click', (event) => {
      event.preventDefault();
      this.load();
    });
  }

  get grid() {
    return document.getElementById('product-grid');
  }

  async load() {
    const url = this.button?.getAttribute('href');
    if (!url || this.isLoading) return;

    this.isLoading = true;
    this.setAttribute('loading', '');
    this.button.setAttribute('aria-busy', 'true');

    try {
      const separator = url.includes('?') ? '&' : '?';
      const response = await fetch(`${url}${separator}section_id=${this.dataset.sectionId}`);
      if (!response.ok) throw new Error(response.status);

      const document_ = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = document_.querySelectorAll('#product-grid > li');
      if (!incoming.length) throw new Error('no items');

      const grid = this.grid;
      const firstNew = incoming[0];
      const fragment = document.createDocumentFragment();
      incoming.forEach((item) => fragment.appendChild(item.cloneNode(true)));
      grid.appendChild(fragment);

      /* The next page's own button carries the page after this one; when the fetched
         page has none, this was the last. */
      const nextButton = document_.querySelector('[data-load-more-button]');
      const nextUrl = nextButton?.getAttribute('href');

      if (nextUrl) {
        this.button.setAttribute('href', nextUrl);
      } else {
        this.button.remove();
        this.button = null;
      }

      /* Products, not cards: with colour cards on, one product fills several grid
         items, and the total beside this count is a product count. Promo tiles carry
         no product id, so they are not counted either way. */
      if (this.shownCount) {
        const ids = new Set(Array.from(grid.querySelectorAll('[data-product-id]'), (card) => card.dataset.productId));
        this.shownCount.textContent = ids.size;
      }

      /* Keyboard and screen reader users need to be told where the new products are,
         not left at the bottom of a grid that silently grew. */
      const appended = grid.children[grid.children.length - incoming.length];
      const focusTarget = appended?.querySelector('a');
      if (focusTarget) {
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
      }
      void firstNew;
    } catch (error) {
      /* Whatever went wrong, the link still points at a page that works. */
      window.location.href = url;
    } finally {
      this.isLoading = false;
      this.removeAttribute('loading');
      this.button?.removeAttribute('aria-busy');
    }
  }
}

customElements.define('mgb-load-more', MgbLoadMore);